import { Injectable } from '@angular/core';
import { IParsedCsvObject } from '../utils.types';

@Injectable({
    providedIn: 'root',
})
export class UtilsService {
    private publicRoles = [
        'Eng - IntApps - Admin - Read',
        'Eng - IntApps - Brokerage Accounts - Read',
        'Eng - IntApps - Brokerage Accounts - Edit',
        'Eng - IntApps - Bank Codes - Read',
        'Eng - IntApps - Bank Codes - Edit',
        'Eng - IntApps - Activity - Edit',
        'Eng - IntApps - Bank Files - Edit',
        'Eng - IntApps - Documents - Edit',
        'Eng - IntApps - User Access - Read',
        'Eng - IntApps - User Access - Edit',
        'Eng - IntApps - Identity Verification - Edit',
        'Eng - IntApps - Vendors - Read',
        'Eng - IntApps - Vendors - Approve',
        'Eng - IntApps - Vendors - Edit',
        'Eng - IntApps - Admin Role Manager - Edit',
        'Eng - IntApps - Admin Role Manager - Read',
        'activity',
        'admin_role_manager_admin',
        'admin_role_manager_editor',
        'admin_role_manager_viewer',
        'auditor',
        'bank_codes_admin',
        'bank_codes_viewer',
        'bank_files',
        'bank_transactions_viewer',
        'brokerage_accounts_admin',
        'brokerage_accounts_viewer',
        'shareholders_admin',
        'shareholders_viewer',
        'user_access_admin',
        'user_access_viewer',
        'vendors_approval',
        'vendors_editor',
        'vendors_viewer',
    ];

    constructor() {}

    public checkValidABA(abaNumber: string): boolean {
        // ABA numbers may only be 9 digits exactly
        if (abaNumber.length !== 9) {
            return false;
        }
        // ABA numbers may only contain numeric digits
        if (abaNumber.search(new RegExp(/\D/g)) !== -1) {
            return false;
        }
        return true;
    }

    public checkValidBIC(bicNumber: string): boolean {
        // BIC numbers may only be 8 OR 11 digits exactly
        if (bicNumber.length !== 8 && bicNumber.length !== 11) {
            return false;
        }
        // BIC numbers may only contain alphanumeric digits
        if (bicNumber.search(new RegExp(/[^a-zA-Z0-9]/g)) !== -1) {
            return false;
        }
        return true;
    }

    public convertCsvRowToArray(text: string): null | string[] {
        const validRegex =
            /^\s*(?:"[^"]*(?:""[\S\s][^"]*)*(?:"")*"|[^,"\s\\]*(?:\s+[^,"\s\\]+)*)\s*(?:,\s*(?:"[^"]*(?:""[\S\s][^"]*)*(?:"")*"|[^,"\s\\]*(?:\s+[^,"\s\\]+)*)\s*)*$/;
        const valueRegex =
            /(?!\s*$)\s*(?:"([^"]*(?:""[\S\s][^"\\]*)*(?:"")*)"|([^,"\s\\]*(?:\s+[^,"\s\\]+)*))\s*(?:,|$)/g;
        // Return NULL if input string is not well formed CSV string.
        if (!validRegex.test(text)) {
            return null;
        }
        // Initialize array to receive values.
        const a = [];
        /**
         * _: the substring that was matched by the regex
         * group1: capturing group 1, string encapsulated in double quotes "
         * group2: capturing group 2, string that is not encapsulated in any quotes
         */
        text.replace(
            valueRegex, // "Walk" the string using replace with callback.
            (_, group1, group2) => {
                if (group1 !== undefined) {
                    // Check for values inside of double quotes "
                    a.push(group1.replace(/""/g, '"')); // Replace all instances of doubled double quotes with single double quote
                } else if (group2 !== undefined) {
                    a.push(group2);
                }
                return ''; // Return empty string.
            },
        );
        // Handle special case of empty last value.
        if (/,\s*$/.test(text)) {
            a.push('');
        }
        return a;
    }

    public checkAllowedKeys(keySource, allowedKeys: string[]): string {
        const mismatchedKeys: string[] = [];
        Object.keys(keySource).forEach((key: string) => {
            if (allowedKeys.indexOf(key) === -1) {
                mismatchedKeys.push(key);
            }
        });
        return mismatchedKeys.length > 0
            ? `Unexpected key(s) encountered. Allowed keys: ${allowedKeys.join(
                  ', ',
              )}. Unexpected key(s): ${mismatchedKeys.join(', ')}`
            : undefined;
    }

    public parseCsvToObject<T>(text: string): IParsedCsvObject<T> {
        let lines = text.split(/\r\n|\r|\n/g);
        lines = lines[lines.length - 1] === '' ? lines.slice(0, lines.length - 1) : lines;
        // If we receive only 1 row or an empty string then we can't parse anything so we just return
        if (lines.length < 2) {
            return { validRows: [] };
        }
        const headers = this.convertCsvRowToArray(lines[0]);
        if (headers === null) {
            return { validRows: [], invalidRows: [{ lineNumber: 0, record: 'nullHeader' }] };
        }
        const rows = lines.slice(1);
        const validRows: { lineNumber: number; record: T }[] = [];
        const invalidRows: { lineNumber: number; record: T }[] = [];
        const duplicateRows: { line: number; matchedLine: number }[] = [];
        const seenRows: { [line: number]: string } = {};
        rows.forEach((record: string, index: number) => {
            const recordArray = this.convertCsvRowToArray(record);
            if (recordArray === null) {
                invalidRows.push({
                    lineNumber: index + 1,
                    record: null,
                });
                // Check for invalid rows which have unequal headers and values
            } else if (recordArray.length !== headers.length) {
                const indexDiff = recordArray.length - headers.length;
                const headersForUnknownValues: string[] = [];
                const valuesForUnknownHeaders: string[] = [];
                if (indexDiff > 0) {
                    for (let i = 0; i < indexDiff; i++) {
                        headersForUnknownValues.push(`unknownHeader${i + 1}`);
                    }
                } else {
                    for (let i = 0; i < Math.abs(indexDiff); i++) {
                        valuesForUnknownHeaders.push(`unknownValue${i + 1}`);
                    }
                }
                invalidRows.push({
                    lineNumber: index + 1,
                    record: this.createObjectFromCsvHeadersAndRows<T>(
                        [...headers, ...headersForUnknownValues],
                        [...recordArray, ...valuesForUnknownHeaders],
                    ),
                });
            } else {
                // Check if duplicate
                let matchedLine: number = -1;
                const isDuplicate = Object.keys(seenRows).some((line) => {
                    if (seenRows[line] === record) {
                        matchedLine = +line;
                        return true;
                    }
                    return false;
                });
                if (isDuplicate) {
                    duplicateRows.push({ line: index + 1, matchedLine: matchedLine + 1 });
                } else {
                    seenRows[index] = record;
                    validRows.push({
                        lineNumber: index + 1,
                        record: this.createObjectFromCsvHeadersAndRows<T>([...headers], [...recordArray]),
                    });
                }
            }
        });
        const returnValue: IParsedCsvObject<T> = { validRows };
        if (invalidRows.length > 0) {
            returnValue.invalidRows = invalidRows;
        }
        if (duplicateRows.length > 0) {
            returnValue.duplicateRows = duplicateRows;
        }
        return returnValue;
    }

    public createObjectFromCsvHeadersAndRows<T>(csvHeaders: string[], csvRows: string[]): T {
        const mappedObject: T = {} as T;
        if (csvHeaders.length === csvRows.length) {
            csvHeaders.forEach((header: string, headerIndex: number) => {
                mappedObject[header] = csvRows[headerIndex];
            });
        } else {
            throw new Error('unable to map headers and rows to object!');
        }
        return mappedObject;
    }

    public removeDisallowedCharacters(inputString: string): string {
        if (typeof inputString !== 'string') {
            throw new Error('Input must be of type string');
        }
        const disallowedCharsRegex = /[^a-zA-Z0-9/\-?:().,'+ ]*/g;
        return inputString.replace(disallowedCharsRegex, '');
    }

    /**
     * A VERY limited date formatter. Only does 24 hour time, no millseconds, and no timezone.
     * The goal was to have something that is easily replaceable with a more robust date/time formatter
     * should that need ever arise.
     * @param desiredFormat: string
     *  yyyy/yy for year (2014/14)
     *  MM/M for month with/without zero padding
     *  dd/d for day with/without zero padding
     *  HH/H for 24-hour-clock hours with/without zero padding
     *  mm/m for minutes with/without zero padding
     *  ss/s for seconds with/without zero padding
     * @param date: Date the date you want formatted
     */
    public formatDateAsStringPattern(desiredFormat: string, date: Date): string {
        let formattedDate = desiredFormat;
        // year
        formattedDate = formattedDate.replace('yyyy', date.getFullYear().toString());
        formattedDate = formattedDate.replace('yy', date.getFullYear().toString().substr(-2));
        // month
        formattedDate = formattedDate.replace('MM', (date.getMonth() + 1).toString().padStart(2, '0'));
        formattedDate = formattedDate.replace('M', (date.getMonth() + 1).toString());
        // day
        formattedDate = formattedDate.replace('dd', date.getDate().toString().padStart(2, '0'));
        formattedDate = formattedDate.replace('d', date.getDate().toString());
        // hours - only works with 24 hours, no AM or PM!
        formattedDate = formattedDate.replace('HH', date.getHours().toString().padStart(2, '0'));
        formattedDate = formattedDate.replace('H', date.getHours().toString());
        // minutes
        formattedDate = formattedDate.replace('mm', date.getMinutes().toString().padStart(2, '0'));
        formattedDate = formattedDate.replace('m', date.getMinutes().toString());
        // seconds
        formattedDate = formattedDate.replace('ss', date.getSeconds().toString().padStart(2, '0'));
        formattedDate = formattedDate.replace('s', date.getSeconds().toString());

        return formattedDate;
    }

    public getArrayIntersection(arrA: (string | number)[], arrB: (string | number)[]): Set<string | number> {
        return new Set(
            arrA.filter((x) => {
                return arrB.includes(x);
            }),
        );
    }

    // Returns the first and last initial of a name
    public getNameInitials(name: string): string {
        const names = name?.trim().split(' ') ?? [];
        if (names.length === 0 || names[0] === '') {
            return '';
        }

        let initials = names[0][0];
        if (names.length > 1) {
            initials += names[names.length - 1][0];
        }
        return initials.toUpperCase();
    }

    public filterToPublicRoles(roles: string[]): string[] {
        const tempRoles = roles.filter((role) => this.publicRoles.includes(role));

        for (let i = 0; i < tempRoles.length; i++) {
            tempRoles[i] = tempRoles[i].replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
        }

        return tempRoles;
    }

    public getEnumKeyByEnumValue = (myEnum, enumValue): string => {
        if (myEnum == null) {
            return enumValue;
        }
        const keys = Object.keys(myEnum).filter((x) => {
            const enumVal = myEnum[x];
            const searchVal = enumValue?.toLowerCase();

            // If the enum value is a string, convert it to lowercase for comparison
            if (typeof enumVal === 'string') {
                return enumVal.toLowerCase() === searchVal;
            }

            // For non-string values, convert to string and compare with lowercase search value
            return String(enumVal) === searchVal;
        });
        return keys.length > 0 ? keys[0] : '';
    };
}
